import React, { useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import styled from 'styled-components';

import { Button, Fade, Icon, OptionGroup } from '@column/column-ui-kit';

import { BusinessEntity } from '~/app/pages/Entities/_partials/BusinessEntity';
import { PersonEntity } from '~/app/pages/Entities/_partials/PersonEntity';
import { ROUTE } from '~/app/routes';
import { CopyInput, EmptyState, Loading, Notification, RenderFields } from '~/components';
import { UnderlineLink } from '~/elements';
import { useNavigate } from '~/lib/navigation';
import { ROUTE as PUBLIC_ROUTE } from '~/public/routes';
import { VerificationStatus } from '~/repositories';
import { Hr, Inner } from '~/styles';

import { Mode, RootEntityOutletContext } from './_util/RootEntityContext';
import { EntityFormType } from './_util/useEntityForm';
import { EntityTypes } from './Edit';

const NotificationWrapper = styled.span`
  display: block;
  margin: 24px var(--page-margin) 0;
`;

const EntityStatusNotification: React.FC<{ status?: VerificationStatus; isSandbox: boolean }> = ({
  status,
  isSandbox,
}) => {
  if (!status) {
    return null;
  }

  if (status === 'VERIFIED') {
    if (isSandbox) {
      return (
        <Notification color="success" variant="light" withClose={false}>
          You've been approved! You can now enter live mode and move real money.
        </Notification>
      );
    }
    return (
      <Notification color="success" variant="light" withClose={false}>
        You've been approved! You can now move real money.
      </Notification>
    );
  }

  if (status === 'MANUAL_REVIEW') {
    return (
      <Notification color="info" variant="light" withClose={false}>
        Our system has flagged that we need to manually review your request. We'll verify this as soon as possible,
        generally within 24 hours.
      </Notification>
    );
  }

  if (status === 'PENDING') {
    return (
      <Notification color="info" variant="light" withClose={false}>
        Your KYC approval is pending. We will email you with updates on your account approval status.
      </Notification>
    );
  }

  if (status === 'DENIED') {
    return (
      <Notification color="danger" variant="light" withClose={false}>
        Unfortunately, this registration was declined. Please{' '}
        <UnderlineLink to={ROUTE.CONTACT_FORM} withoutArrow>
          contact us
        </UnderlineLink>{' '}
        if you have any questions.
      </Notification>
    );
  }

  return null;
};

export const PageRootEntity: React.FC = () => {
  const {
    mode,
    formType,
    sandboxRootEntity,
    liveRootEntity,
    isSandboxLoading,
    isLiveLoading,
    isSandbox,
    handleSubmit,
    setFormType,
    resetForm,
  } = useOutletContext<RootEntityOutletContext>();
  const navigate = useNavigate();

  const [displayMode, setDisplayMode] = useState<Mode>(mode);
  const [displayFormType, setDisplayFormType] = useState<EntityTypes>();
  const previousFormTypeRef = useRef<EntityTypes | undefined>(undefined);
  const previousModeRef = useRef<Mode>(mode);

  const rootEntityResponse = displayMode === Mode.Sandbox ? sandboxRootEntity : liveRootEntity;
  const isRootEntityLoading = displayMode === Mode.Sandbox ? isSandboxLoading : isLiveLoading;
  const currentModeLoading = mode === Mode.Sandbox ? isSandboxLoading : isLiveLoading;
  const hasRootEntity = Boolean(rootEntityResponse);
  const isLiveRootEntity = displayMode === Mode.Live && hasRootEntity;

  useEffect(() => {
    if (mode !== previousModeRef.current) {
      previousModeRef.current = mode;
    } else if (displayMode !== mode) {
      setDisplayMode(mode);
    }
  }, [mode, displayMode]);

  useEffect(() => {
    if (formType && !displayFormType) {
      setDisplayFormType(formType);
    } else if (!formType && !displayFormType && !isRootEntityLoading && !hasRootEntity) {
      setFormType(EntityFormType.Person);
    }
  }, [formType, displayFormType, isRootEntityLoading, hasRootEntity, setFormType]);

  useEffect(() => {
    if (formType !== previousFormTypeRef.current) {
      previousFormTypeRef.current = formType;
      if (hasRootEntity && formType) {
        setDisplayFormType(formType);
      }
    } else if (displayFormType !== formType && !hasRootEntity && formType) {
      setDisplayFormType(formType);
    }
  }, [formType, hasRootEntity, displayFormType]);

  useEffect(() => {
    if (hasRootEntity && formType) {
      resetForm(formType, rootEntityResponse ?? null);
      previousFormTypeRef.current = formType;
    }
  }, [resetForm, formType, rootEntityResponse, hasRootEntity]);

  const handleModeFadeOut = () => {
    if (mode !== displayMode && formType) {
      const newRootEntityResponse = mode === Mode.Sandbox ? sandboxRootEntity : liveRootEntity;
      resetForm(formType, newRootEntityResponse ?? null);
      setDisplayMode(mode);
    }
  };

  const handlePersonFormFadeOut = () => {
    if (!hasRootEntity && formType === EntityFormType.Business && displayFormType === EntityFormType.Person) {
      resetForm(EntityFormType.Business, null);
      setDisplayFormType(EntityFormType.Business);
    }
  };

  const handleBusinessFormFadeOut = () => {
    if (!hasRootEntity && formType === EntityFormType.Person && displayFormType === EntityFormType.Business) {
      resetForm(EntityFormType.Person, null);
      setDisplayFormType(EntityFormType.Person);
    }
  };

  useEffect(() => {
    if (!hasRootEntity && displayFormType) {
      resetForm(displayFormType, null);
    }
  }, [hasRootEntity, displayFormType, resetForm]);

  const isFormInitialized = displayFormType !== undefined;

  return (
    <Loading isLoading={currentModeLoading || !isFormInitialized}>
      <Fade show={mode === displayMode && isFormInitialized} onExited={handleModeFadeOut}>
        {displayMode === Mode.Live && !hasRootEntity && (
          <EmptyState headline="No root entity" text={'Create your Root Entity by opening a Commercial Account.'}>
            <Button
              onClick={() => navigate(`${PUBLIC_ROUTE.REGISTER_OPEN_BANK_ACCOUNT}`)}
              size="small"
              icon={<Icon.Plus />}
            >
              Create Commercial Account
            </Button>
          </EmptyState>
        )}

        {(displayMode === Mode.Sandbox || hasRootEntity) && (
          <>
            {displayMode === Mode.Live && hasRootEntity && (
              <NotificationWrapper>
                <EntityStatusNotification status={rootEntityResponse?.verificationStatus} isSandbox={isSandbox} />
              </NotificationWrapper>
            )}

            {RenderFields<{ type: EntityTypes; id?: string }>({
              sections: [
                {
                  fields: [
                    {
                      id: 'id',
                      label: 'ID',
                      hide: !hasRootEntity,
                      children: ({ value }) => <CopyInput value={(value as string) ?? ''} isMonospaced />,
                    },
                    {
                      id: 'type',
                      label: 'Entity Type',
                      children: () => (
                        <OptionGroup orientation={OptionGroup.Orientation.Horizontal}>
                          {[
                            { label: 'Person', value: EntityFormType.Person },
                            { label: 'Business', value: EntityFormType.Business },
                          ].map(({ label, value }) => {
                            const name = label;

                            return (
                              <OptionGroup.Option
                                key={name}
                                isActive={formType === value}
                                isDisabled={hasRootEntity}
                                onClick={() => {
                                  if (hasRootEntity) return;
                                  setFormType(value);
                                }}
                              >
                                <OptionGroup.Option.Radio />
                                <OptionGroup.Option.Label>{name}</OptionGroup.Option.Label>
                              </OptionGroup.Option>
                            );
                          })}
                        </OptionGroup>
                      ),
                    },
                  ],
                },
              ],
            })}
            <Hr />

            <Fade
              show={displayFormType === EntityFormType.Person && (hasRootEntity || formType === EntityFormType.Person)}
              onExited={handlePersonFormFadeOut}
            >
              {displayFormType === EntityFormType.Person && (
                <PersonEntity
                  id={rootEntityResponse?.personDetails ? rootEntityResponse.id : undefined}
                  disableForm={isLiveRootEntity}
                />
              )}
            </Fade>
            <Fade
              show={
                displayFormType === EntityFormType.Business && (hasRootEntity || formType === EntityFormType.Business)
              }
              onExited={handleBusinessFormFadeOut}
            >
              {displayFormType === EntityFormType.Business && (
                <BusinessEntity
                  id={rootEntityResponse?.businessDetails ? rootEntityResponse.id : undefined}
                  disableForm={isLiveRootEntity}
                />
              )}
            </Fade>

            {!isLiveRootEntity && (
              <Inner pt={0}>
                <Button onClick={handleSubmit} size="small" icon={!hasRootEntity && <Icon.Plus />}>
                  {hasRootEntity ? 'Save Changes' : 'Create Root Entity'}
                </Button>
              </Inner>
            )}
          </>
        )}
      </Fade>
    </Loading>
  );
};
